/**
 * AtmosphereGroup — the tunnelmakuva gallery at the top of the report.
 *
 * Up to 8 slots; only populated ones render. Two are shown by default with a
 * "näytä kaikki" toggle, because a workshop can produce six or more photos and
 * an uncropped gallery otherwise pushes the report's first real section below
 * the fold. The lightbox always walks the full set.
 *
 * Images are never cropped: the grid flexes between 1 and 3 columns by width
 * and each image keeps its natural aspect ratio. Rows can therefore be uneven
 * in height — the honest trade for not cutting people out of their own photos.
 *
 * Slot 1 falls back to the Kuutio brand image (see DEFAULT_ATMOSPHERE) so every
 * report shows something here even before Drive photos are populated. A
 * report_edits value always wins.
 *
 * Field keys:  intro.atmosphere.<n>.image_url + .alt
 */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import ImageLightbox from './ImageLightbox';
import { useReportEdits } from './ReportEditsContext';
import { normalizeImageUrl } from './imageUrl';
import { useTranslation } from '../../hooks/useTranslation.jsx';

// Up to 8 atmosphere slots; only populated ones render.
const SLOTS = [0, 1, 2, 3, 4, 5, 6, 7];

// How many show before the reader asks for more.
const INITIAL_VISIBLE = 3;

/**
 * Defaults so every report shows the atmosphere row, even before anyone has
 * populated workshop photos from Drive.
 *
 * Slot 1 is the Kuutio brand image and is the same for every workshop, so it
 * belongs in code rather than being re-populated per instance. Slots 0 and 2
 * are the workshop's own tunnelmakuvat and have no sensible default — they
 * stay empty until populated, and empty slots are skipped.
 *
 * A report_edits value always wins; this only fills the gap.
 */
const DEFAULT_ATMOSPHERE = {
  1: {
    url: 'https://www.sitra.fi/wp-content/uploads/2025/11/kuutio-featureimage-mustapohja.jpg',
    alt: 'Kuutio',
  },
};

/* Uniform height, natural widths, centered horizontally. On desktop /
 * tablet (≥640px) all 3 images fit in a single row — the height clamp
 * below is tuned so the wide Kuutio brand image plus two photo-style
 * tunnelmakuvat stay under typical report main-column widths. On phone
 * portraits we let them wrap so they don't get squashed below ~120px. */
const ShowAllRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 12px;
  @media print { display: none; }
`;

const ShowAllButton = styled.button`
  background: none;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 999px;
  padding: 6px 18px;
  font-size: 14px;
  font-weight: 600;
  color: #367ABA;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover { background: rgba(54, 122, 186, 0.08); }
`;

const Row = styled.div`
  /* Justified row: each slot's width is proportional to its own aspect ratio
   * (see Slot's flex-grow), so heights line up, nothing is cropped, and the
   * row always fills the column — however many images are in it.
   *
   * A fixed 3-column grid left a visible blank cell whenever fewer than three
   * images were shown, which looked broken rather than sparse. */
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  margin: 12px 0 24px;
  width: 100%;

  @media (min-width: 640px) {
    flex-direction: row;
    flex-wrap: nowrap;
  }
`;

const Slot = styled.button`
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  display: block;
  cursor: zoom-in;
  appearance: none;
  font: inherit;
  line-height: 0;

  img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 2px;
  }

  @media (min-width: 640px) {
    /* Width follows the image's own aspect ratio, so every slot in the row
     * ends up the same height without cropping. Ratio is measured on load;
     * 1.4 is a reasonable landscape guess for the first paint. */
    flex: ${p => p.$ratio || 1.4} 1 0;
    min-width: 0;
    width: auto;
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

export default function AtmosphereGroup() {
  const { get } = useReportEdits();
  const { t } = useTranslation();
  const [lightboxStart, setLightboxStart] = useState(null);
  const [expanded, setExpanded] = useState(false);
  // width/height per slot, measured on load — drives the justified widths
  const [ratios, setRatios] = useState({});

  // Build the gallery from populated slots only. Empty slots are skipped
  // so the lightbox chevrons only walk loaded images.
  const slots = SLOTS.map(i => {
    const edited = normalizeImageUrl(get(`intro.atmosphere.${i}.image_url`, ''));
    const url = edited || DEFAULT_ATMOSPHERE[i]?.url || '';
    const alt = get(
      `intro.atmosphere.${i}.alt`,
      DEFAULT_ATMOSPHERE[i]?.alt || `Tunnelmakuva ${i + 1}`,
    );
    return { i, url, alt };
  }).filter(s => s.url);

  const openAt = (url) => {
    const idx = slots.findIndex(s => s.url === url);
    setLightboxStart(idx >= 0 ? idx : 0);
  };

  if (slots.length === 0) return null;

  // A workshop can produce many photos; showing them all pushes the report's
  // first real section below the fold. Show a couple, let the reader ask for
  // the rest. The lightbox always walks the full set.
  const visible = expanded ? slots : slots.slice(0, INITIAL_VISIBLE);
  const hiddenCount = slots.length - visible.length;

  return (
    <>
      <Row>
        {visible.map(({ i, url, alt }) => (
          <Slot
            key={i}
            $ratio={ratios[i]}
            onClick={() => openAt(url)}
            aria-label={`Avaa ${alt} suurempana`}
          >
            <img
              src={url}
              alt={alt}
              onLoad={(e) => {
                const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                if (!w || !h) return;
                setRatios(prev => (prev[i] ? prev : { ...prev, [i]: w / h }));
              }}
            />
          </Slot>
        ))}
      </Row>

      {hiddenCount > 0 && (
        <ShowAllRow>
          <ShowAllButton type="button" onClick={() => setExpanded(true)}>
            {t('report.stats.showAllPhotos', { count: slots.length })}
          </ShowAllButton>
        </ShowAllRow>
      )}
      {expanded && slots.length > INITIAL_VISIBLE && (
        <ShowAllRow>
          <ShowAllButton type="button" onClick={() => setExpanded(false)}>
            {t('report.stats.showFewerPhotos')}
          </ShowAllButton>
        </ShowAllRow>
      )}

      {lightboxStart !== null && (
        <ImageLightbox
          images={slots.map(s => ({ url: s.url, alt: s.alt, caption: s.alt }))}
          initialIndex={lightboxStart}
          onClose={() => setLightboxStart(null)}
        />
      )}
    </>
  );
}
